import { extendVariants, Link as HeroLink, LinkProps } from '@heroui/react';
import { AnchorHTMLAttributes, type ElementType } from 'react';
import { twMerge } from 'tailwind-merge';

import { HeroBaseProps } from '../utils';

// For all Link props see https://v2.heroui.com/docs/components/link
export const Link = extendVariants(
  (
    props: HeroBaseProps<
      LinkProps,
      'color' | 'underline' | 'isExternal' | 'download' | 'isDisabled' | 'showAnchorIcon' | 'className'
    > & {
      as?: ElementType;
      color?: Extract<LinkProps['color'], 'foreground' | 'primary'>;
      underline?: Extract<LinkProps['underline'], 'always' | 'hover' | 'none'>;
      /**
       * Only used when as is RouterLink
       */
      to?: string;
    } & Pick<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>,
    // `text-[length:inherit]` lets inline text links match their surrounding text.
    // It is written as a font-size utility (not the arbitrary property
    // `[font-size:inherit]`) so tailwind-merge recognizes it as a font-size class:
    // an explicit size from a consumer (e.g. `<Button as={Link} size="sm">`) then
    // wins because `props.className` comes last in the merge.
    // `as="button"` renders a real <button>, which the user agent paints with its own background,
    // border and padding. Anchors have none of those, so the reset is inert for them.
  ) => (
    <HeroLink
      {...props}
      className={twMerge('text-[length:inherit] cursor-pointer border-0 bg-transparent p-0', props.className)}
    />
  ),
  {
    variants: {
      color: {
        foreground: 'text-secondary-950',
        primary: 'text-primary-600',
      },
    },
  },
);
